import type {UserRole} from "@cdx/shared/auth/scopes";
import type {Root} from "../../cdx-models/Root";
import type {User, UserId} from "../../cdx-models/User";

export const roleToSortIndex = {
  owner: 0,
  admin: 1,
  guest: 3,
  observer: 4,
  disabled: 5,
  service: 4,
  deleted: 5,
  token: 5,
};

export const paidRoles: UserRole[] = ["owner", "admin", "guest"];

export const roleToLabel: {[role in UserRole]: string} = {
  owner: "Owner",
  admin: "Admin",
  guest: "Staff",
  observer: "Observer",
  disabled: "Disabled",
  service: "Disabled",
  deleted: "Disabled",
  token: "API Token",
};

export const getUserRole = (
  root: Root
):
  | {isLoaded: false; role: null; userId: null | UserId; user: User | null}
  | {isLoaded: true; role: UserRole | null; userId: null; user: null}
  | {isLoaded: true; role: UserRole | null; userId: UserId; user: User} => {
  const {account, loggedInUser: user} = root;
  if (!user) return {isLoaded: true, role: null, userId: null, user: null};
  const userId = user.$meta.get("id", null);
  if (!userId) return {isLoaded: false, role: null, userId: null, user};
  const roles = account.$meta.find("roles", {userId});
  if (roles.length === 0) return {isLoaded: true, role: null, userId, user};
  const role = roles[0].$meta.get("role", null);
  if (!role) return {isLoaded: false, role: null, userId, user};
  return {isLoaded: true, role, userId, user};
};

export const roleIsAtLeast = (role: UserRole | null, targetRole: UserRole) => {
  return role && roleToSortIndex[role] <= roleToSortIndex[targetRole];
};

export const isProjectSpecificRole = (role: UserRole): boolean => {
  return role === "observer" || role === "guest";
};

export const accessibleOrgRoles = ({
  root,
  role = ["observer", "guest", "admin", "owner"],
}: {
  root: Root;
  role?: UserRole[];
}) =>
  root.loggedInUser
    ? root.loggedInUser.$meta.find("accountRoles", {
        role,
        account: {isDisabled: false},
      })
    : [];

/** Returns the role shown in onboarding texts, including "producer". A label, not a permission. */
export const getRoleInclProducer = (root: Root) => {
  const {isLoaded, role, user} = getUserRole(root);
  if (!isLoaded) return null;
  if (roleIsAtLeast(role, "admin")) return "admin";
  if (role === "observer" || !user) return role;
  const accountId = root.account.$meta.get("id", null);
  const producesSomewhere =
    accountId &&
    user.$meta.exists("withProjectAccess", {projectRole: "producer", project: {accountId}}, null);
  return producesSomewhere ? "producer" : role;
};
